import { Component, OnDestroy, OnInit } from '@angular/core';
import { AuthServerProvider, IUser } from 'app/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { UserService } from '../../services/user.service';
import { IUpdateObject, UpdateObject } from 'app/core/user/updateObject.model';
import { faEyeSlash, faEye } from '@fortawesome/free-solid-svg-icons';
import { DataSharingService } from 'app/layouts/homepage/services/data-sharing.service';
import { CookieService } from 'ngx-cookie';
import { HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

export enum PassType {
    OLD,
    NEW,
    CONFIRM
}

function firstCharacter(control: FormControl) {
    const value = control.value;
    const CHARACTER_ONLY_REGEX = new RegExp('[A-Za-z]');
    if (value) {
        if (!CHARACTER_ONLY_REGEX.test(control.value[0])) {
            return {
                emailDomain: {
                    parsedDomain: value
                }
            };
        }
    }
    return null;
}

@Component({
    selector: 'jhi-change-password',
    templateUrl: './change-password.component.html',
    styleUrls: ['../../../../../content/scss/styles.scss']
})
export class ChangePasswordComponent implements OnInit, OnDestroy {
    user: IUser;
    passwordForm: FormGroup;
    submitted = false;
    appUrl = '';
    successMsg: string = null;
    faEyeSlash = faEyeSlash;
    faEye = faEye;
    PassType = PassType;
    showOld = false;
    showNew = false;
    showConfirm = false;
    errorMsg: any;
    isFirstLogin: boolean;
    loading: boolean;
    showPasswordRules = false;
    private subscription: Subscription;
    canAccessForm: boolean = false;  // Controls if user can access the form
    pwdlessWarningMessage: string = null;  // Warning message for non-EXTRA_UE users

    constructor(
        private formBuilder: FormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private userService: UserService,
        private jwtService: AuthServerProvider,
        private dataSharingService: DataSharingService,
        private cookieService: CookieService,
        private translateService: TranslateService
    ) {
        this.loading = true;
        this.createForm();
        this.appUrl = this.route.snapshot.queryParamMap.get('appUrl');
    }

    ngOnInit() {
        this.subscription = this.route.data.subscribe(routeData => {
            this.isFirstLogin = routeData.isFirstLogin;

            this.jwtService
                .authorize(this.isFirstLogin)
                .toPromise()
                .then(res => {
                    this.getInitData();
                })
                .catch(err => {});
        });
    }

    private createForm() {
        this.passwordForm = this.formBuilder.group(
            {
                oldPassword: ['', [Validators.required]],
                newPassword: ['', [Validators.required]],
                confirmNewPassword: ['', [Validators.required]]
            },
            {
                validators: matchValidator
            }
        );
    }

    get f() {
        return this.passwordForm.controls;
    }

    get fOldPassword() {
        return this.passwordForm.get('oldPassword');
    }
    get fNewPassword() {
        return this.passwordForm.get('newPassword');
    }
    get fConfirmNewPassword() {
        return this.passwordForm.get('confirmNewPassword');
    }

    onSubmit() {
        // stop here if form is invalid
        if (this.passwordForm.invalid) {
            return;
        }

        const pwdToChange: IUpdateObject = new UpdateObject(
            this.fOldPassword.value,
            this.fNewPassword.value,
            this.fConfirmNewPassword.value
        );
        if (this.isFirstLogin && !this.isUserPopulated()) {
            // store pwdToChange for later
            this.userService.pwdtoChange = pwdToChange;
            this.router.navigate(['primoAccessoModificaProfilo']);
        } else {
            this.submitted = true;
            this.userService
                .changePassword(pwdToChange)
                .toPromise()
                .then(res => {
                    if (res && res.body && res.body.message) {
                        this.successMsg = res.body.message;
                    } else {
                        this.successMsg = 'Operazione effettuata con successo';
                    }
                })
                .catch(err => {
                    this.verifyValidationError(err);
                })
                .finally(() => {
                    this.submitted = false;
                });
        }
    }

    verifyValidationError(err: HttpErrorResponse) {
        if (err && err.error) {
            if (err.error.message === 'error.validation') {
                this.errorMsg = 'Si è verificato un errore nella validazione dei campi.';
            } else {
                this.errorMsg = err.error.message;
            }
        }
    }

    onReset() {
        this.submitted = false;
        this.populateForm();
    }

    private isUserPopulated() {
        return (
            ((!!this.user.email && this.user.emailVerified) || (!!this.user.alternativeEmail && this.user.alternativeEmailVerified)) &&
            (this.user.challengeAnswerSetted && !!this.user.challengeQuestion)
        );
    }

    private getInitData() {
        this.userService
            .getLogged()
            .toPromise()
            .then(res => {
                this.user = res.body;
                
                // Check if user can access the form based on userType
                // Only EXTRA_UE users can access when passwordless mode is enabled
                if (this.user.showPwdlessWarning) {
                    // User is not EXTRA_UE, show only warning
                    this.canAccessForm = false;
                    // Load warning message from backend (same as homepage)
                    this.loadWarningMessage();
                } else {
                    // User can access the form
                    this.canAccessForm = true;
                    this.populateForm();
                }
            })
            .catch(err => {})
            .finally(() => {
                this.loading = false;
            });
    }

    private loadWarningMessage() {
        this.userService.getUserTypeWarning('passwordless').subscribe(
            (message: string) => {
                this.pwdlessWarningMessage = message;
            },
            error => {
                console.warn('Could not load passwordless warning message', error);
                this.pwdlessWarningMessage = this.translateService.instant('error.functionality.not.available');
            }
        );
    }

    private populateForm() {
        this.passwordForm.patchValue({
            oldPassword: '',
            newPassword: '',
            confirmNewPassword: ''
        });
        this.passwordForm.markAsPristine();
        this.passwordForm.markAsUntouched();
    }

    backToApp() {
        this.router.navigate(['/']);
    }

    changeShowPassword(type: PassType) {
        if (type === PassType.OLD) {
            this.showOld = !this.showOld;
        }
        if (type === PassType.NEW) {
            this.showNew = !this.showNew;
        }
        if (type === PassType.CONFIRM) {
            this.showConfirm = !this.showConfirm;
        }
    }

    ngOnDestroy() {
        this.jwtService.logout();
        if(this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    get isDesktop(): boolean {
        return this.dataSharingService.isDesktop();
    }

    redirect() {
        if (this.isFirstLogin) {
            this.userService
                .logout()
                .toPromise()
                .then(res => {
                    // remove jwt token client-side
                    this.jwtService.logout();

                    const backUrl = this.cookieService.get('backUrl');
                    if (backUrl) {
                        window.location.href = backUrl;
                    } else {
                        this.router.navigate(['/']);
                    }
                });
        } else {
            this.router.navigate(['/']);
        }
    }
}

export const matchValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const newPassword = control.get('newPassword');
    const confirmNewPassword = control.get('confirmNewPassword');

    return newPassword && confirmNewPassword && newPassword.value !== confirmNewPassword.value ? { fieldMatch: true } : null;
};
